<template>
  <div class="irp-partner-data-section" data-testid="irp-partner-data-section">
    <IRPPartnerFilterBar
      :is-expanded="isEveryRowOpen"
      :matched-partner-uuids="matchedPartnerUuids"
      @toggle-expansion="setAllExpanded"
    />

    <IRPRecommendedPartnersTableSkeleton v-if="isInitialLoading" />
    <BittsEmptyState
      v-else-if="hasError"
      :font-awesome-icon="ERROR_STATE.icon"
      :title="ERROR_STATE.title"
      :description="ERROR_STATE.description"
      :button="RETRY_BUTTON"
      data-testid="irp-partner-data-section-error-state"
      @button-clicked="refetch"
    />
    <BittsEmptyState
      v-else-if="!partners.length"
      :font-awesome-icon="emptyIcon"
      title="No partner data"
      :description="emptyDescription"
      data-testid="irp-partner-data-section-empty-state"
    />
    <template v-else>
      <div class="irp-partner-data-section__container">
        <IRPPartnerRow
          v-for="partner in partners"
          :key="partner.partner_organization_uuid"
          :partner="partner"
          :columns="HEADER_COLUMNS[section]"
          :is-open="isRowOpen(partner)"
          @update:is-open="
            (isOpen) => setRowOpen(partner.partner_organization_uuid, isOpen)
          "
        >
          <slot :partner="partner" />
        </IRPPartnerRow>
      </div>
      <BittsPaginator
        v-if="hasMultiplePages"
        class="justify-end"
        :page="pagination.page"
        :last-page="lastPage"
        @page-click="setPage"
      />
    </template>
  </div>
</template>

<script setup lang="ts" generic="Section extends RecordSectionType">
import { BittsEmptyState, BittsPaginator } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { isEqual } from 'lodash';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useIndividualRecordStore } from '@/stores';
import type {
  RecordPartnerType,
  RecordSectionType,
} from '@/types/individual_record';

import type { IRPPartnerColumnType } from './irp';
import IRPPartnerFilterBar from './IRPPartnerFilterBar.vue';
import IRPPartnerRow from './IRPPartnerRow.vue';
import IRPRecommendedPartnersTableSkeleton from './IRPRecommendedPartnersTableSkeleton.vue';
import { filtersFromQuery, hasFilterQueryChanged } from './recordFilterQuery';

const { section } = defineProps<{
  section: Section;
}>();

defineSlots<{
  default(props: { partner: RecordPartnerType<Section> }): unknown;
}>();

const EMPTY_DESCRIPTIONS: Record<RecordSectionType, string> = {
  master: 'No partner shares data on this record.',
  opportunities: 'No partner shares opportunities on this record.',
  contacts: 'No partner shares contacts on this record.',
};

const EMPTY_ICONS: Record<RecordSectionType, BittsIcon> = {
  master: ['fak', 'company'],
  opportunities: ['fak', 'opportunity'],
  contacts: ['fak', 'contacts'],
};

const HEADER_COLUMNS: Record<RecordSectionType, IRPPartnerColumnType[]> = {
  master: ['score', 'owner', 'populations'],
  opportunities: ['score', 'opportunities'],
  contacts: ['score', 'contacts'],
};

const LEAD_ICON: BittsIcon = ['fak', 'person'];

const ERROR_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'Partner data could not load',
  description: 'Something went wrong on our end. Try again in a moment.',
  icon: ['fak', 'warning'],
};

const RETRY_BUTTON = {
  text: 'Try again',
  type: 'neutral',
  variant: 'outline',
} as const;

const individualRecordStore = useIndividualRecordStore();

const state = computed(() => individualRecordStore.sections[section]);
const partners = computed(
  () => state.value.partners.items as RecordPartnerType<Section>[],
);
const pagination = computed(() => state.value.partners.pagination);
const lastPage = computed(() => pagination.value.last_page ?? 1);
const hasMultiplePages = computed(() => lastPage.value > 1);
const isInitialLoading = computed(
  () => state.value.isLoading && !state.value.hasLoaded,
);
const hasError = computed(() => !!state.value.error);

const emptyDescription = computed(() => EMPTY_DESCRIPTIONS[section]);
const emptyIcon = computed<BittsIcon>(() =>
  section === 'master' && individualRecordStore.mdmType === 'lead'
    ? LEAD_ICON
    : EMPTY_ICONS[section],
);

const matchedPartnerUuids = computed(() =>
  partners.value.map((partner) => partner.partner_organization_uuid),
);

const isAllExpanded = ref(false);
const openOverrides = ref<Record<string, boolean>>({});

function isRowOpen(partner: RecordPartnerType<Section>) {
  return (
    openOverrides.value[partner.partner_organization_uuid] ??
    isAllExpanded.value
  );
}

const isEveryRowOpen = computed(
  () => !!partners.value.length && partners.value.every(isRowOpen),
);

function clearOverrides() {
  openOverrides.value = {};
}

function setRowOpen(uuid: string, isOpen: boolean) {
  openOverrides.value[uuid] = isOpen;
}

function setAllExpanded(isExpanded: boolean) {
  isAllExpanded.value = isExpanded;
  clearOverrides();
}

function setPage(page: number) {
  return individualRecordStore.setPage(section, page);
}

function refetch() {
  return individualRecordStore.fetchSection(section, { isForced: true });
}

const route = useRoute();

watch(
  () => route.query,
  (query, previousQuery) => {
    if (!hasFilterQueryChanged(query, previousQuery)) return;
    individualRecordStore.setFilters(section, filtersFromQuery(query));
  },
);

const urlFilters = filtersFromQuery(route.query);
if (!isEqual(urlFilters, individualRecordStore.sections[section].filters)) {
  individualRecordStore.setFilters(section, urlFilters);
}
individualRecordStore.fetchSection(section);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-partner-data-section {
  @apply flex flex-col gap-16;
}

.irp-partner-data-section__container {
  @apply flex flex-col gap-4 rounded-16 border border-neutral-border p-8;
}

.irp-partner-data-section__container :deep(.irp-partner-row:nth-child(even)) {
  @apply bg-neutral-background-weak;
}
</style>
